/**
 * Shared subscriptions (ניהול מוצרים → ניהול מנויים ותוכניות) types, option lists
 * and the pricing algorithm.
 *
 * As in `customer.types`, the enum *values are the Hebrew labels themselves* so a
 * stored value can be rendered (and exported to Excel) as-is, and filter/dropdown
 * options reuse the same list everywhere.
 *
 * Item prices are **per week**. Billing happens monthly, by the number of Shabbatot
 * in the month — see `monthlyTotal`.
 *
 * A catalog item's `price` is only its *base* price: `priceTiers` may discount it by
 * quantity, so what a line actually costs per unit is always `effectiveUnitPrice`.
 *
 * This file is dependency-free (no React/MUI/browser imports) so the backend can
 * import it via the `@malash-office/types/subscriptions` deep alias. The pricing
 * helpers live here on purpose: the form preview and the persisted total must come
 * from one implementation.
 */
import { Customer, SelectOptionLite } from './customer.types';

const toOptions = (values: readonly string[]): SelectOptionLite[] => values.map(v => ({ label: v, value: v }));

// --- סטטוס מנוי ---
/**
 * Where every subscription starts. It is deliberately *not* פעיל: a subscription is created
 * before anyone has checked it, and only פעיל is billed monthly, generates weekly orders and
 * counts as an active subscription on the stat cards. Moving it to פעיל is a human decision,
 * taken from the status column on the list.
 */
export const SUBSCRIPTION_NEW = 'חדש';
export const SUBSCRIPTION_ACTIVE = 'פעיל';
export const SUBSCRIPTION_PAUSED = 'מושהה';
export const SUBSCRIPTION_CANCELLED = 'מבוטל';
export const SUBSCRIPTION_OVERDUE = 'איחור תשלום';
/** A landing-page checkout paid by card, waiting for the customer to save the card at Cardcom. */
export const SUBSCRIPTION_PENDING_PAYMENT = 'ממתין לתשלום';
export const SUBSCRIPTION_STATUSES = [
  SUBSCRIPTION_NEW,
  SUBSCRIPTION_ACTIVE,
  SUBSCRIPTION_PAUSED,
  SUBSCRIPTION_CANCELLED,
  SUBSCRIPTION_OVERDUE,
  SUBSCRIPTION_PENDING_PAYMENT
] as const;
export const SUBSCRIPTION_STATUS_OPTIONS = toOptions(SUBSCRIPTION_STATUSES);

/**
 * Created by a checkout and not yet a "real" subscription: never generates orders, is never
 * billed, and is counted nowhere. It stays until the card arrives, with no expiry — the office
 * sees how old it is and leaves it through the status column; the checkout alone enters
 * {@link SUBSCRIPTION_PENDING_PAYMENT}.
 */
export const CHECKOUT_UNFINISHED_STATUSES = [SUBSCRIPTION_PENDING_PAYMENT] as const;
export const isCheckoutUnfinished = (status: string): boolean =>
  (CHECKOUT_UNFINISHED_STATUSES as readonly string[]).includes(status);

/**
 * What a person may set by hand — everything except "waiting for payment", a state only a checkout
 * creates. Drives the status dropdowns and the Joi rules on create / status / batch update.
 */
export const SUBSCRIPTION_MANUAL_STATUSES = SUBSCRIPTION_STATUSES.filter(
  status => status !== SUBSCRIPTION_PENDING_PAYMENT
);
export const SUBSCRIPTION_MANUAL_STATUS_OPTIONS = toOptions(SUBSCRIPTION_MANUAL_STATUSES);

/** A joiner can only ride along with a live shipment. */
export const isJoinableStatus = (status: string): boolean =>
  status !== SUBSCRIPTION_CANCELLED && !isCheckoutUnfinished(status);

/**
 * Chip colour per status, as **theme palette keys** — the design spec hardcodes hex
 * values, but this project only ever colours from `theme.palette.*`.
 */
export const SUBSCRIPTION_STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  [SUBSCRIPTION_NEW]: 'info',
  [SUBSCRIPTION_ACTIVE]: 'success',
  [SUBSCRIPTION_PAUSED]: 'warning',
  [SUBSCRIPTION_CANCELLED]: 'default',
  [SUBSCRIPTION_OVERDUE]: 'error',
  [SUBSCRIPTION_PENDING_PAYMENT]: 'warning'
};

// --- תדירות מנוי ---
export const PLAN_FREQUENCY_WEEKLY = 'שבועי';
export const PLAN_FREQUENCY_MONTHLY = 'חודשי';
export const PLAN_FREQUENCY_QUARTERLY = 'רבעוני';
export const PLAN_FREQUENCIES = [PLAN_FREQUENCY_WEEKLY, PLAN_FREQUENCY_MONTHLY, PLAN_FREQUENCY_QUARTERLY] as const;
export const PLAN_FREQUENCY_OPTIONS = toOptions(PLAN_FREQUENCIES);

// --- אופן יצירת הזמנות ---
export const ORDER_CREATION_MANUAL = 'ידני';
export const ORDER_CREATION_AUTOMATIC = 'אוטומטי';
export const ORDER_CREATION_MODES = [ORDER_CREATION_MANUAL, ORDER_CREATION_AUTOMATIC] as const;
export const ORDER_CREATION_OPTIONS = toOptions(ORDER_CREATION_MODES);

// --- שיטת אספקה ---
export const DELIVERY_HOME = 'משלוח עד הבית';
export const DELIVERY_SELF_PICKUP = 'איסוף עצמי';
export const DELIVERY_PICKUP_POINT = 'איסוף מנקודת איסוף';
export const DELIVERY_JOIN_EXISTING = 'מצטרף למשלוח קיים';
/**
 * Reserved for the digital subscription (stage 5): no shipment at all. Deliberately *not* in
 * {@link SUBSCRIPTION_DELIVERY_METHODS}, so the admin dropdown and order generation do not change;
 * only a landing page's checkout config may say "no delivery", and a subscriptions page cannot be
 * published that way until stage 5 defines how it is produced and billed.
 */
export const DELIVERY_NONE = 'ללא אספקה';
export const SUBSCRIPTION_DELIVERY_METHODS = [
  DELIVERY_HOME,
  DELIVERY_SELF_PICKUP,
  DELIVERY_PICKUP_POINT,
  DELIVERY_JOIN_EXISTING
] as const;
export const SUBSCRIPTION_DELIVERY_METHOD_OPTIONS = toOptions(SUBSCRIPTION_DELIVERY_METHODS);

// --- חברת שילוח ---
/**
 * Who physically carries a משלוח עד הבית. A fixed pair for now, like `PICKUP_POINTS` —
 * this moves to a table when the לוגיסטיקה module lands.
 */
export const SHIPPING_COMPANY_KATZ = 'כץ משלוחים';
export const SHIPPING_COMPANY_SHIPPING = 'שיפינג';
export const SHIPPING_COMPANIES = [SHIPPING_COMPANY_KATZ, SHIPPING_COMPANY_SHIPPING] as const;
export const SHIPPING_COMPANY_OPTIONS = toOptions(SHIPPING_COMPANIES);

/** Only משלוח עד הבית is carried by a shipping company; every other method is collected. */
export const requiresShippingCompany = (deliveryMethod: string): boolean => deliveryMethod === DELIVERY_HOME;

/**
 * Whether the label carries the pack list — the date and the עלון-against-quantity rows.
 *
 * **No package prints a תעודת משלוח.** A carrier's package (משלוח עד הבית, כץ or שיפינג) leaves
 * with the carrier's label alone: what is inside the box is already written into the shipment
 * remark both carriers are sent (`describeShipmentContents`), so a second sheet only repeated it.
 *
 * Every other package — איסוף עצמי, מצטרף למשלוח קיים, נקודת איסוף — is labelled by us, and has no
 * remark anywhere, so its label answers "what is in here" by itself and reads as a receipt for the
 * contents. A joiner's **parent** is a carrier's package like any other: carrier labels, no list.
 *
 * Shared so the packing screen and the document service cannot disagree about what is printed.
 */
export const labelCarriesPackList = (deliveryMethod: string): boolean => !requiresShippingCompany(deliveryMethod);

/**
 * Whether a carrier's package also gets our pack-list label, printed after the carrier's own.
 *
 * כץ only: their remark field is 80 characters (see `describeShipmentContents`), so a real
 * order's contents do not fit in it and the box cannot be checked against its label. Every כץ
 * package therefore leaves with both — the carrier's shipping label, and ours with the items.
 * שיפינג's remark has the room, so it keeps the carrier's label alone.
 */
export const carrierAddsPackListLabel = (provider: string): boolean => provider === SHIPPING_COMPANY_KATZ;

/**
 * Chip colours per delivery method, as **theme palette paths** — this project never hardcodes a
 * colour, so these are fed straight to `sx={{ bgcolor, color }}` and resolve through the theme.
 * A tinted background with the same hue's main as the text, which is how every status chip in
 * the dashboard already reads.
 */
export const SUBSCRIPTION_DELIVERY_METHOD_COLOR: Record<string, { bg: string; fg: string }> = {
  [DELIVERY_HOME]: { bg: 'info.A200', fg: 'info.main' },
  [DELIVERY_SELF_PICKUP]: { bg: 'success.A100', fg: 'success.main' },
  [DELIVERY_PICKUP_POINT]: { bg: 'warning.A100', fg: 'warning.main' },
  [DELIVERY_JOIN_EXISTING]: { bg: 'warning.A100', fg: 'warning.main' }
};

export const deliveryMethodColor = (deliveryMethod: string): { bg: string; fg: string } =>
  SUBSCRIPTION_DELIVERY_METHOD_COLOR[deliveryMethod] ?? { bg: 'grey.300', fg: 'text.secondary' };

/**
 * Only איסוף מנקודת איסוף carries a pickup-point code.
 *
 * The method itself is **not in actual use** — כץ, 2026-08-30: "נקודת איסוף לא קיים". It is kept
 * rather than removed because orders, subscriptions and their columns already reference it; it is
 * simply never chosen. מצטרף למשלוח קיים is the live one.
 */
export const DELIVERY_METHODS_REQUIRING_PICKUP_ID: readonly string[] = [DELIVERY_PICKUP_POINT];

export const requiresPickupId = (deliveryMethod: string): boolean =>
  DELIVERY_METHODS_REQUIRING_PICKUP_ID.includes(deliveryMethod);

/**
 * מצטרף למשלוח קיים carries a link to another **subscription** instead — the one whose delivery
 * it rides along with. It used to borrow `pickupPointId` and a pickup-point code, which pointed
 * at nothing: the warehouse could not tell who a joiner travels with, so its package could not be
 * packed next to theirs.
 */
export const requiresJoinTarget = (deliveryMethod: string): boolean => deliveryMethod === DELIVERY_JOIN_EXISTING;

/**
 * איסוף עצמי carries the same link **optionally**: one shaliach collects for another, so the
 * second package is packed next to the first exactly as a מצטרף למשלוח קיים is. It stays
 * איסוף עצמי — the package is still collected, not shipped — and the link is what makes it a
 * joiner. See {@link isJoiner}.
 */
export const allowsJoinTarget = (deliveryMethod: string): boolean =>
  requiresJoinTarget(deliveryMethod) || deliveryMethod === DELIVERY_SELF_PICKUP;

/**
 * Which methods a joiner of this method may point at. Never a joiner itself — that would be a
 * chain rather than "these two travel together" — and an איסוף עצמי joiner may only join
 * another איסוף עצמי: whoever comes to the warehouse takes both packages, which cannot be true
 * of a package that leaves on a van or waits at a pickup point.
 */
export const allowedJoinTargetMethods = (deliveryMethod: string): readonly string[] =>
  deliveryMethod === DELIVERY_SELF_PICKUP
    ? [DELIVERY_SELF_PICKUP]
    : [DELIVERY_HOME, DELIVERY_SELF_PICKUP, DELIVERY_PICKUP_POINT];

/**
 * Whether this subscription (or order) actually rides along with another one — the single
 * question every screen asks, now that being a joiner is either the method or the link:
 * מצטרף למשלוח קיים always is one, איסוף עצמי only once it is linked.
 */
export const isJoiner = (deliveryMethod: string, joinsId?: number | null): boolean =>
  requiresJoinTarget(deliveryMethod) || (deliveryMethod === DELIVERY_SELF_PICKUP && joinsId != null);

/**
 * "מצטרף לאיסוף קיים" — a radio choice on the edit forms, **not** a stored method. The office
 * asked for it to sit beside מצטרף למשלוח קיים rather than hide as a checkbox under איסוף עצמי;
 * underneath it is still an איסוף עצמי plus a link (see {@link allowsJoinTarget}), so nothing
 * about the data, the API or the warehouse changes.
 */
export const DELIVERY_CHOICE_JOIN_SELF_PICKUP = 'מצטרף לאיסוף קיים';
export const DELIVERY_CHOICE_OPTIONS = toOptions([...SUBSCRIPTION_DELIVERY_METHODS, DELIVERY_CHOICE_JOIN_SELF_PICKUP]);

/** The radio the form shows for a stored method and its "joins a self pickup" flag. */
export const deliveryChoiceOf = (deliveryMethod: string, selfPickupJoin: boolean): string =>
  deliveryMethod === DELIVERY_SELF_PICKUP && selfPickupJoin ? DELIVERY_CHOICE_JOIN_SELF_PICKUP : deliveryMethod;

/** The other way round: what a chosen radio stores. */
export const deliveryMethodOfChoice = (choice: string): { deliveryMethod: string; selfPickupJoin: boolean } =>
  choice === DELIVERY_CHOICE_JOIN_SELF_PICKUP
    ? { deliveryMethod: DELIVERY_SELF_PICKUP, selfPickupJoin: true }
    : { deliveryMethod: choice, selfPickupJoin: false };

/**
 * The shipping address, field by field. Every one of them is required on a משלוח עד הבית —
 * a courier cannot deliver to a partial address, and the warehouse has no way to complete it
 * later. `driverNotes` is the one exception: it is guidance, not an address.
 */
export const REQUIRED_ADDRESS_FIELDS = [
  { field: 'city', label: 'עיר' },
  { field: 'street', label: 'רחוב' },
  { field: 'houseNumber', label: 'מספר בית' },
  { field: 'recipientName', label: 'שם המקבל' },
  { field: 'recipientPhone', label: 'טלפון המקבל' }
] as const;

export type RequiredAddressField = (typeof REQUIRED_ADDRESS_FIELDS)[number]['field'];

/** Only משלוח עד הבית carries an address at all — every other method is collected. */
export const requiresAddress = (deliveryMethod: string): boolean => deliveryMethod === DELIVERY_HOME;

/**
 * The required address fields left blank, as their Hebrew labels. Empty for every delivery
 * method that isn't משלוח עד הבית. Shared so the form's submit rule and the API's rejection
 * are the same rule, stated once.
 */
export const missingAddressFields = (
  deliveryMethod: string,
  address?: Partial<Record<RequiredAddressField, string | null>> | null
): string[] => {
  if (!requiresAddress(deliveryMethod)) return [];
  return REQUIRED_ADDRESS_FIELDS.filter(({ field }) => !address?.[field]?.trim()).map(({ label }) => label);
};

/**
 * Pickup points. A constant list for now — nothing in the subscriptions scope manages
 * them; this moves to a table when the לוגיסטיקה module lands.
 */
export const PICKUP_POINTS: SelectOptionLite[] = [
  { value: 'PT-101', label: 'PT-101 — מרכז חב"ד תל אביב' },
  { value: 'PT-114', label: 'PT-114 — בית חב"ד ירושלים' },
  { value: 'PT-128', label: 'PT-128 — נקודת חלוקה חיפה' },
  { value: 'PT-142', label: 'PT-142 — בית חב"ד באר שבע' }
];

/** Label for a stored pickup-point id, falling back to the raw id if it is no longer listed. */
export const pickupPointLabel = (pickupPointId?: string | null): string =>
  PICKUP_POINTS.find(point => point.value === pickupPointId)?.label ?? pickupPointId ?? '—';

/** Where an איסוף עצמי subscription is collected from. Shown on the card and on a delivery order. */
export const SELF_PICKUP_NOTICE =
  'האיסוף מתבצע ממחסן המרכז לעזרי שליחות בכפר חב"ד (בסככה), צעירי חב"ד 1. בכל שבוע החל מיום רביעי בשעה 22:00.';

// --- שיטת תשלום ---
export const PAYMENT_VIA_ACCOUNT = 'תשלום דרך החשבון במרכז לעזרי שליחות';
export const PAYMENT_BY_CARD = 'תשלום בכרטיס אשראי';
/**
 * Paid elsewhere (a תוכנית חיצונית, a תחנה בארץ) or simply given free. The prices stay on the
 * subscription and its orders — they record what was handed out — but nothing is ever billed:
 * the monthly run only picks up `PAYMENT_VIA_ACCOUNT` and `PAYMENT_BY_CARD`.
 */
export const PAYMENT_FREE = 'ללא תשלום';
export const SUBSCRIPTION_PAYMENT_METHODS = [PAYMENT_VIA_ACCOUNT, PAYMENT_BY_CARD, PAYMENT_FREE] as const;
/** Whether a subscription paid this way is ever billed. */
export const isChargedPaymentMethod = (paymentMethod: string): boolean => paymentMethod !== PAYMENT_FREE;
export const SUBSCRIPTION_PAYMENT_METHOD_OPTIONS = toOptions(SUBSCRIPTION_PAYMENT_METHODS);

// --- תעריפי משלוח עד הבית ---
export const DELIVERY_FULL_PRICE = 32;
export const DELIVERY_DISCOUNT_PRICE = 15;
export const DELIVERY_DISCOUNT_THRESHOLD = 100;
export const DELIVERY_FREE_THRESHOLD = 200;

/** The minimum a pricing calculation needs to know about one line of a subscription. */
export interface SubscriptionLineInput {
  quantity: number;
  unitPrice: number;
  /** Whether the catalog item participates in the delivery promo (מבצע משלוח). */
  deliveryPromo: boolean;
}

export interface SubscriptionTotals {
  /** Σ quantity × unitPrice, per week. */
  itemsSubtotal: number;
  /** Σ quantity over every line. */
  totalQuantity: number;
  /** Σ quantity over promo lines only — the figure the delivery thresholds compare against. */
  promoQuantity: number;
  deliveryCost: number;
  /** itemsSubtotal + deliveryCost, per week. */
  weeklyTotal: number;
}

export const promoQuantity = (lines: readonly SubscriptionLineInput[]): number =>
  lines.reduce((sum, line) => (line.deliveryPromo ? sum + line.quantity : sum), 0);

export const totalQuantity = (lines: readonly SubscriptionLineInput[]): number =>
  lines.reduce((sum, line) => sum + line.quantity, 0);

/**
 * Delivery cost per week. Only משלוח עד הבית is charged, and the thresholds count
 * **promo items only** (the on-screen copy is "פריטים המשתתפים במבצע").
 */
export const calcDeliveryCost = (deliveryMethod: string, promoQty: number): number => {
  if (deliveryMethod !== DELIVERY_HOME) return 0;
  if (promoQty >= DELIVERY_FREE_THRESHOLD) return 0;
  return promoQty >= DELIVERY_DISCOUNT_THRESHOLD ? DELIVERY_DISCOUNT_PRICE : DELIVERY_FULL_PRICE;
};

export const calcSubscriptionTotals = (
  lines: readonly SubscriptionLineInput[],
  deliveryMethod: string
): SubscriptionTotals => {
  const itemsSubtotal = lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
  const promoQty = promoQuantity(lines);
  const deliveryCost = calcDeliveryCost(deliveryMethod, promoQty);
  return {
    itemsSubtotal,
    totalQuantity: totalQuantity(lines),
    promoQuantity: promoQty,
    deliveryCost,
    weeklyTotal: itemsSubtotal + deliveryCost
  };
};

// --- הנחות כמות (מדרגות מחיר) ---

/**
 * One rung of a catalog item's quantity-discount ladder: from `minQuantity` units and up,
 * **every** unit costs `unitPrice` (volume pricing, not marginal) — 250 units on a 200-unit
 * rung of 0.45 costs 250 × 0.45, not 199 × base + 51 × 0.45.
 */
export interface PriceTier {
  /** כמות מינימלית להפעלת המדרגה. */
  minQuantity: number;
  /** מחיר ליחידה מהכמות הזאת ומעלה. */
  unitPrice: number;
}

/** Ladders are edited by hand in one dialog; a bound keeps the stored JSON small and readable. */
export const MAX_PRICE_TIERS = 10;

/** The rung a quantity reaches — the highest one it clears — or null when the base price applies. */
export const tierFor = (tiers: readonly PriceTier[] | null | undefined, quantity: number): PriceTier | null =>
  (tiers ?? []).reduce<PriceTier | null>(
    (best, tier) => (quantity >= tier.minQuantity && (!best || tier.minQuantity > best.minQuantity) ? tier : best),
    null
  );

/**
 * What one unit actually costs at this quantity. The single answer to "what price is this?" —
 * the form preview, the snapshot taken on save and a credit line all go through it, so a
 * quantity can never be priced two different ways.
 */
export const effectiveUnitPrice = (
  item: { price: number; priceTiers?: readonly PriceTier[] | null },
  quantity: number
): number => tierFor(item.priceTiers, quantity)?.unitPrice ?? item.price;

/** A hand-typed price on one line — the shape both the subscription form and an order line send. */
export interface UnitPriceOverride {
  /** True once a human typed the price. While false the price is derived and follows the quantity. */
  priceOverridden?: boolean;
  unitPrice?: number;
}

/**
 * What one unit of a line actually costs: a hand-typed price when one was set, otherwise
 * {@link effectiveUnitPrice} at this quantity.
 *
 * The override is **sticky on purpose**. A price agreed with one shaliach for one product is a
 * decision, not a calculation, so changing the quantity must not quietly recompute it away —
 * which is exactly what would happen if the override were just a starting value. Clearing it is
 * an explicit act (the reset button on the line), and only then does the price follow the
 * catalog and its הנחות כמות again.
 *
 * One implementation, shared by the form preview and the server's snapshot, for the same reason
 * `quantityViolation` is: the two can never disagree about what a line costs.
 */
export const resolvedUnitPrice = (
  item: { price: number; priceTiers?: readonly PriceTier[] | null },
  quantity: number,
  override?: UnitPriceOverride | null
): number =>
  override?.priceOverridden ? Math.max(0, Number(override.unitPrice) || 0) : effectiveUnitPrice(item, quantity);

/**
 * `null` when the ladder is legal; otherwise the Hebrew reason, ready to render as-is (helper
 * text in the dialog, 400 message from the API) — the same `quantityViolation` arrangement.
 *
 * Prices must never climb: this is a **discount** ladder, so a rung above the base price (or
 * above the rung below it) is a typo that would quietly overcharge, not a configuration.
 */
export const priceTiersViolation = (basePrice: number, tiers: readonly PriceTier[]): string | null => {
  if (tiers.length > MAX_PRICE_TIERS) return `ניתן להגדיר עד ${MAX_PRICE_TIERS} מדרגות כמות`;

  const sorted = [...tiers].sort((a, b) => a.minQuantity - b.minQuantity);
  let previousPrice = basePrice;

  for (const [index, tier] of sorted.entries()) {
    if (!Number.isInteger(tier.minQuantity) || tier.minQuantity < 2) {
      return 'כמות המדרגה חייבת להיות מספר שלם, 2 ומעלה';
    }
    if (!Number.isFinite(tier.unitPrice) || tier.unitPrice < 0) {
      return 'מחיר המדרגה חייב להיות מספר חיובי';
    }
    if (index > 0 && tier.minQuantity === sorted[index - 1].minQuantity) {
      return `הוגדרו שתי מדרגות לאותה כמות (${tier.minQuantity})`;
    }
    if (tier.unitPrice > previousPrice) {
      return `מחיר המדרגה ${tier.minQuantity} גבוה מהמחיר שלפניה — הנחת כמות חייבת להוזיל`;
    }
    previousPrice = tier.unitPrice;
  }
  return null;
};

/** The ladder as one line of text: `200+ → 0.45 · 1000+ → 0.35`. Empty string when there is none. */
export const priceTiersSummary = (tiers: readonly PriceTier[] | null | undefined): string =>
  [...(tiers ?? [])]
    .sort((a, b) => a.minQuantity - b.minQuantity)
    .map(tier => `${tier.minQuantity}+ → ${tier.unitPrice}`)
    .join(' · ');

// --- אופן מכירה: מכפלות או כמות מינימום ---

/**
 * How a catalog item constrains the ordered quantity. The two rules are **mutually
 * exclusive by design**: an item is either sold in multiples (`saleMultiple` > 1) or from
 * a minimum order quantity — never both, so an order quantity is never caught between two
 * rules. `saleMultiple: 1` and `minOrderQuantity: null` both mean "unconstrained".
 */
export interface ItemQuantityRule {
  saleMultiple: number;
  minOrderQuantity: number | null;
}

export const sellsInMultiples = (rule: ItemQuantityRule): boolean => rule.saleMultiple > 1;

export const hasMinOrderQuantity = (rule: ItemQuantityRule): boolean =>
  rule.minOrderQuantity !== null && rule.minOrderQuantity > 0;

/** True when an item was configured with both rules — impossible to create, but see the rename migration. */
export const hasConflictingQuantityRules = (rule: ItemQuantityRule): boolean =>
  sellsInMultiples(rule) && hasMinOrderQuantity(rule);

export const QUANTITY_RULES_CONFLICT_MESSAGE =
  'לא ניתן להגדיר גם מכפלת מכירה וגם כמות מינימום להזמנה — יש לבחור אחד מהם';

/**
 * `null` when the quantity satisfies the item's rule; otherwise the Hebrew reason, ready
 * to render as-is (field helper text on the card, 400 message from the API). One
 * implementation so the form and the server can never disagree on what is legal.
 */
export const quantityViolation = (rule: ItemQuantityRule, quantity: number): string | null => {
  if (hasMinOrderQuantity(rule) && quantity < (rule.minOrderQuantity as number)) {
    return `כמות מינימום להזמנה: ${rule.minOrderQuantity}`;
  }
  if (sellsInMultiples(rule) && quantity % rule.saleMultiple !== 0) {
    return `הכמות חייבת להיות בכפולות של ${rule.saleMultiple}`;
  }
  return null;
};

/**
 * Opening quantity for a line the user adds by hand: the smallest legal quantity that is
 * not below `fallback`. An item with no rule simply gets `fallback`.
 */
export const initialQuantityFor = (rule: ItemQuantityRule, fallback: number): number => {
  if (hasMinOrderQuantity(rule)) return Math.max(rule.minOrderQuantity as number, fallback);
  if (sellsInMultiples(rule)) return Math.ceil(fallback / rule.saleMultiple) * rule.saleMultiple;
  return fallback;
};

/** Number of Shabbatot (Saturdays) in a month. `month` is 1-12. */
export const countShabbatot = (year: number, month: number): number => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay(); // 0 = Sunday … 6 = Saturday
  const firstSaturday = 1 + ((6 - firstDayOfWeek + 7) % 7);
  return Math.floor((daysInMonth - firstSaturday) / 7) + 1;
};

/** What the customer is actually billed for a month: the weekly cost × its Shabbatot. */
export const monthlyTotal = (weeklyTotal: number, year: number, month: number): number =>
  weeklyTotal * countShabbatot(year, month);

// --- Entities (API shapes) ---

export interface SubscriptionPlanItem {
  id: number;
  planId: number;
  name: string;
  /** מק״ט — optional catalog number, not unique across plans. */
  sku: string | null;
  /** מחיר ליחידה להזמנה — what one unit costs before any quantity discount. */
  price: number;
  /**
   * הנחות כמות — the quantity-discount ladder over `price`. Empty means the base price
   * applies at every quantity. See {@link effectiveUnitPrice}.
   */
  priceTiers: PriceTier[];
  /** מכפלת מכירה — quantities must be whole multiples of it. 1 → unconstrained. */
  saleMultiple: number;
  /** Counts towards the delivery-promo thresholds (מבצע משלוח). */
  deliveryPromo: boolean;
  active: boolean;
  /**
   * כמות מינימום להזמנה — the floor for this item's quantity, and the quantity it is
   * pre-filled with when the plan is picked. `null` → no minimum, and not pre-added.
   * Mutually exclusive with `saleMultiple` — see `ItemQuantityRule`.
   */
  minOrderQuantity: number | null;
  /**
   * סדר ליקוט — the item's place on the shelves the warehouse walks. Order lines are listed in it
   * on the packing card, our labels and the carriers' remarks. `null` → after every numbered item.
   */
  sortOrder: number | null;
  /** Joined for display in the catalog table. */
  planName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  active: boolean;
  /** תדירות המנוי — how often this plan is meant to ship. One of `PLAN_FREQUENCIES`. */
  frequency: string;
  /**
   * אופן יצירת הזמנות — one of `ORDER_CREATION_MODES`. `ידני` means orders appear only when
   * someone runs the הפקת הזמנות dialog; `אוטומטי` marks the plan as *intended* to be
   * generated on a schedule (no scheduler runs yet — see the orders screen notice).
   */
  orderCreation: string;
  /** Joined count for the catalog table. */
  itemCount?: number;
  items?: SubscriptionPlanItem[];
  createdAt?: string;
  updatedAt?: string;
}

export interface SubscriptionItem {
  id: number;
  subscriptionId: number;
  planItemId: number;
  quantity: number;
  /**
   * Price snapshot taken when the subscription was last saved — the item's
   * {@link effectiveUnitPrice} at *this* line's quantity, so re-pricing the catalog or
   * changing its ladder never silently re-bills existing subscriptions.
   *
   * When {@link SubscriptionItem.priceOverridden} is set it is instead the price a human typed.
   */
  unitPrice: number;
  /**
   * A price agreed for **this** subscription and **this** product — the app's answer to
   * "דריסת מחיר ללקוח ספציפי". It survives quantity changes and catalog re-pricing, and it is
   * what every order generated from this subscription freezes. See {@link resolvedUnitPrice}.
   */
  priceOverridden: boolean;
  planItem?: Pick<
    SubscriptionPlanItem,
    'id' | 'name' | 'price' | 'priceTiers' | 'saleMultiple' | 'minOrderQuantity' | 'deliveryPromo' | 'active'
  > &
    Partial<Pick<SubscriptionPlanItem, 'sortOrder'>>;
}

export interface SubscriptionAddress {
  city: string | null;
  street: string | null;
  houseNumber: string | null;
  recipientName: string | null;
  recipientPhone: string | null;
  driverNotes: string | null;
}

/** The personal details shown (read-only) on the subscription card — all owned by the customer. */
export type SubscriptionCustomer = Pick<
  Customer,
  'id' | 'name' | 'phone' | 'email' | 'priorityId' | 'shluchimDashboardId' | 'customerType'
>;

/**
 * The little a joiner needs to say about the subscription it joins: whose delivery it is, and
 * how that delivery arrives. Enough to label a picker option or a chip, without pulling the
 * whole subscription — and without ever recursing into *its* join target.
 */
export interface JoinTargetSummary {
  id: number;
  deliveryMethod: string;
  customerName: string;
}

/**
 * תגית מנוי — a free label on one subscription ("קירור", "לא להשאיר בחוץ"), many per subscription.
 *
 * The subscription's own, not the customer's: a shaliach with two subscriptions can tag each one
 * differently, and nothing here reaches the customer tags that גביה and the mailing read. Nothing
 * in the code branches on a tag — it only filters the orders and the packing queue.
 */
export interface SubscriptionTag {
  id: number;
  name: string;
}

export interface Subscription {
  id: number;
  customerId: number;
  planId: number;
  status: string;
  deliveryMethod: string;
  pickupPointId: string | null;
  /** Set only for `משלוח עד הבית` — one of {@link SHIPPING_COMPANIES}. */
  shippingCompany: string | null;
  /**
   * How many boxes the package takes — the packing dialog starts from it. Null until the packer
   * first answers, and cleared again whenever the plan or the items change.
   */
  defaultPackagesCount: number | null;
  /** Set for `מצטרף למשלוח קיים`, and optionally for `איסוף עצמי` — see {@link allowsJoinTarget}. */
  joinsSubscriptionId: number | null;
  /** The joined subscription's own summary, so a picker or a card can name it without a second call. */
  joinsSubscription?: JoinTargetSummary | null;
  address: SubscriptionAddress;
  /** Reserved — there is no upload pipeline yet, so nothing writes this field. */
  locationPhotoUrl: string | null;
  paymentMethod: string;
  cardHolderName: string | null;
  /** Last 4 digits only. The full number and the CVV are never stored or returned. */
  cardLast4: string | null;
  cardExpiry: string | null;
  /**
   * Whether a Cardcom token is saved. The token itself never leaves the server — this flag is
   * the only thing the client needs, and it is what decides whether the monthly billing job
   * can charge this subscription at all.
   */
  hasCardToken: boolean;
  startedAt: string;
  cancelledAt: string | null;
  createdAt?: string;
  updatedAt?: string;
  items: SubscriptionItem[];
  plan?: Pick<SubscriptionPlan, 'id' | 'name' | 'active'>;
  customer?: SubscriptionCustomer | null;
  /** An account-billed subscription whose customer has no Priority number yet — see `Customer.awaitingPriorityId`. */
  awaitingPriorityId?: boolean;
  /** By name. Returned by the single-subscription read only; the list rows leave it out. */
  tags?: SubscriptionTag[];
  /** Set when a landing-page checkout created the subscription. Read-only on the card. */
  landingPage?: { id: number; name: string } | null;
  /** The checkout's answers that have no column of their own (names, ID, institution, notes, custom fields). */
  checkoutFields?: Record<string, unknown> | null;
  /** Server-computed, from the persisted line snapshots. */
  totals: SubscriptionTotals;
}

// --- API request/response shapes ---

/** Query params for the server-side subscription list (all optional, mirror the URL). */
export interface SubscriptionListParams {
  page?: string | number;
  rowsPerPage?: string | number;
  sortField?: string;
  sortOrder?: string;
  /** Free text over the customer's name, phone, ids and email, and the plan's name. */
  searchTerm?: string;
  status?: string;
  planId?: string | number;
  /** One customer's subscriptions — the credit form uses it to offer the shaliach's own. */
  customerId?: number;
  /** Subscriptions created by one landing page (with `status`, the page's "נטושים"). */
  landingPageId?: string | number;
  paymentMethod?: string;
  deliveryMethod?: string;
}

export interface SubscriptionListResponse {
  rows: Subscription[];
  count: number;
}

/** The four stat cards on the list screen. */
export interface SubscriptionStats {
  activeCount: number;
  newThisMonth: number;
  cancelledThisMonth: number;
  /** Σ weekly total over active subscriptions. */
  weeklyIncome: number;
}

export interface UpsertSubscriptionItem {
  planItemId: number;
  quantity: number;
  /**
   * Sent only alongside `priceOverridden` — the server derives the price itself otherwise, so a
   * client cannot set a price by accident. See {@link resolvedUnitPrice}.
   */
  unitPrice?: number;
  priceOverridden?: boolean;
}

export interface UpsertSubscriptionBody {
  customerId: number;
  planId: number;
  status?: string;
  deliveryMethod: string;
  pickupPointId?: string | null;
  /** Only kept for `משלוח עד הבית` — see {@link requiresShippingCompany}. */
  shippingCompany?: string | null;
  /**
   * Omitted means "leave it alone". A save that changes the plan or the items clears it unless the
   * body sets a new value — see `resolveDefaultPackagesCount`.
   */
  defaultPackagesCount?: number | null;
  joinsSubscriptionId?: number | null;
  address?: Partial<SubscriptionAddress>;
  paymentMethod: string;
  /**
   * The full tag set — it replaces whatever the subscription carried. Omitted means "leave the
   * tags alone", so a caller that never shows them cannot wipe them by saving.
   */
  tagIds?: number[];
  /**
   * No card fields. A card reaches this system only through Cardcom's hosted page — see
   * {@link CardSessionReturnTo} — so a full number or a CVV never travels in this body.
   */
  items: UpsertSubscriptionItem[];
}

/**
 * עדכון מרובה in the מנויים list (`PATCH /subscriptions/batch`). Every field is optional and only
 * the ones present are applied. Tags are add / remove lists rather than {@link UpsertSubscriptionBody.tagIds}'s
 * replacement set, so a bulk edit never wipes tags the user didn't touch.
 */
export interface SubscriptionBatchPatch {
  status?: string;
  tagsAdd?: number[];
  tagsRemove?: number[];
}

/**
 * Which page Cardcom should send the browser back to when the hosted card page closes.
 *
 * `new` is the registration flow: the subscription was created moments ago and the user has
 * never seen it, so they come back to the wizard and get its success screen. `subscription`
 * is a card attached to a subscription that already exists, so they come back to its card.
 */
export const CARD_RETURN_TO_NEW = 'new';
export const CARD_RETURN_TO_SUBSCRIPTION = 'subscription';
export const CARD_RETURN_TO_VALUES = [CARD_RETURN_TO_NEW, CARD_RETURN_TO_SUBSCRIPTION] as const;
export type CardSessionReturnTo = (typeof CARD_RETURN_TO_VALUES)[number];

export interface CreateCardSessionBody {
  returnTo?: CardSessionReturnTo;
}

export interface UpsertPlanBody {
  name: string;
  active?: boolean;
  frequency?: string;
  orderCreation?: string;
}

export interface UpsertPlanItemBody {
  planId: number;
  name: string;
  sku?: string | null;
  price?: number;
  priceTiers?: PriceTier[];
  saleMultiple?: number;
  deliveryPromo?: boolean;
  active?: boolean;
  minOrderQuantity?: number | null;
  sortOrder?: number | null;
}
